import { copyTextToClipboard } from '../utils/formatListClipboard';
import { freezeAnchorRect, resolveAnchorRect } from './translateAnchor';

export type TranslateBubbleState = 'loading' | 'result' | 'error';

let bubbleEl: HTMLElement | null = null;
let positionListener: (() => void) | null = null;
let lastPositionRect: DOMRect | null = null;
/** Keep bubble visible after selection clears (until user dismisses or taps outside). */
let bubblePinned = false;

export function isTranslateBubblePinned(): boolean {
  return bubblePinned;
}

function ensureBubble(): HTMLElement {
  if (!bubbleEl) {
    const el = document.createElement('div');
    el.id = 'translate-selection-bubble';
    el.className = 'translate-bubble hidden';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    bubbleEl = el;
  }
  document.body.appendChild(bubbleEl);
  return bubbleEl;
}

function isCoarsePointer(): boolean {
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}

function viewportBox(): { top: number; left: number; width: number; height: number } {
  const vv = window.visualViewport;
  return {
    top: vv?.offsetTop ?? 0,
    left: vv?.offsetLeft ?? 0,
    width: vv?.width ?? window.innerWidth,
    height: vv?.height ?? window.innerHeight,
  };
}

function positionBubble(rect: DOMRect): void {
  if (!bubbleEl || bubbleEl.classList.contains('hidden')) return;
  const anchor = resolveAnchorRect(rect);
  lastPositionRect = anchor;

  const margin = 10;
  const gap = 8;
  const vp = viewportBox();

  bubbleEl.style.right = '';
  bubbleEl.style.width = '';
  bubbleEl.style.maxWidth = `min(22rem, calc(${Math.round(vp.width)}px - ${margin * 2}px))`;

  const bubbleH = bubbleEl.offsetHeight || 96;
  const bubbleW = bubbleEl.offsetWidth || Math.min(280, vp.width - margin * 2);

  const selTop = anchor.top;
  const selBottom = anchor.bottom;
  const selCenterX = anchor.left + Math.max(anchor.width, 4) / 2;

  let top: number;
  if (isCoarsePointer()) {
    top = selBottom + gap;
    if (top + bubbleH > vp.top + vp.height - margin) {
      top = selTop - bubbleH - gap;
    }
  } else {
    top = selTop - bubbleH - gap;
    if (top < vp.top + margin) {
      top = selBottom + gap;
    }
  }

  top = Math.max(vp.top + margin, Math.min(top, vp.top + vp.height - bubbleH - margin));

  let left = selCenterX - bubbleW / 2;
  left = Math.max(vp.left + margin, Math.min(left, vp.left + vp.width - bubbleW - margin));

  bubbleEl.style.top = `${Math.round(top)}px`;
  bubbleEl.style.left = `${Math.round(left)}px`;
}

function schedulePosition(rect: DOMRect): void {
  const anchor = freezeAnchorRect(resolveAnchorRect(rect));
  positionBubble(anchor);
  requestAnimationFrame(() => {
    positionBubble(anchor);
    requestAnimationFrame(() => positionBubble(anchor));
  });
}

function bindPositionListeners(): void {
  if (positionListener) return;
  positionListener = () => {
    if (lastPositionRect) positionBubble(lastPositionRect);
  };
  window.visualViewport?.addEventListener('resize', positionListener);
  window.visualViewport?.addEventListener('scroll', positionListener);
  window.addEventListener('resize', positionListener);
  window.addEventListener('scroll', positionListener, { capture: true, passive: true });
}

function unbindPositionListeners(): void {
  if (!positionListener) return;
  window.visualViewport?.removeEventListener('resize', positionListener);
  window.visualViewport?.removeEventListener('scroll', positionListener);
  window.removeEventListener('resize', positionListener);
  window.removeEventListener('scroll', positionListener, true);
  positionListener = null;
  lastPositionRect = null;
}

function showBubble(
  rect: DOMRect,
  build: (el: HTMLElement) => void,
  options?: { pin?: boolean }
): void {
  const el = ensureBubble();
  build(el);
  el.classList.remove('hidden');
  bubblePinned = options?.pin !== false;
  bindPositionListeners();
  schedulePosition(rect);
}

export function hideTranslateBubble(): void {
  bubblePinned = false;
  bubbleEl?.classList.add('hidden');
  unbindPositionListeners();
}

/** Touch / mobile: user taps Translate after selecting (browser keeps its own menu). */
export function showTranslateBubblePrompt(
  rect: DOMRect,
  phrase: string,
  onTranslate: () => void
): void {
  showBubble(rect, el => {
    el.innerHTML = `
    <p class="translate-bubble-phrase">${escapeHtml(phrase)}</p>
    <div class="translate-bubble-actions">
      <button type="button" class="btn-primary btn-sm translate-bubble-go">Translate</button>
      <button type="button" class="btn-ghost-dark btn-sm translate-bubble-dismiss">Dismiss</button>
    </div>
  `;
    el.querySelector('.translate-bubble-go')!.addEventListener('click', () => {
      onTranslate();
    });
    el.querySelector('.translate-bubble-dismiss')!.addEventListener('click', hideTranslateBubble);
  });
}

export type TranslatePhraseChoice = {
  /** Highlighted text or word under the finger. */
  primary: string;
  /** Full sentence around that point. */
  sentence: string;
};

function truncateLabel(s: string, max = 36): string {
  const t = s.trim().replace(/\s+/g, ' ');
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function primaryChoiceLabel(phrase: string): string {
  return /\s/.test(phrase.trim()) ? 'Selection' : 'Word';
}

function samePhrase(a: string, b: string): boolean {
  return a.trim().replace(/\s+/g, ' ') === b.trim().replace(/\s+/g, ' ');
}

/** Word/selection vs full sentence when they differ; otherwise a single Translate action. */
export function showTranslateBubbleChoicePrompt(
  rect: DOMRect,
  choice: TranslatePhraseChoice,
  onPick: (phrase: string) => void
): void {
  const primary = choice.primary.trim().replace(/\s+/g, ' ');
  const sentence = choice.sentence.trim().replace(/\s+/g, ' ');

  if (primary.length < 2 && sentence.length < 2) return;
  if (primary.length < 2 || samePhrase(primary, sentence)) {
    const phrase = primary.length >= 2 ? primary : sentence;
    showTranslateBubblePrompt(rect, phrase, () => onPick(phrase));
    return;
  }

  const primaryLabel = primaryChoiceLabel(primary);
  showBubble(rect, el => {
    el.innerHTML = `
    <p class="translate-bubble-phrase">${escapeHtml(primary)}</p>
    <div class="translate-bubble-actions translate-bubble-actions--choice">
      <button type="button" class="btn-primary btn-sm translate-bubble-primary" title="${escapeAttr(primary)}">
        ${primaryLabel}: ${escapeHtml(truncateLabel(primary))}
      </button>
      <button type="button" class="btn-ghost-dark btn-sm translate-bubble-sentence" title="${escapeAttr(sentence)}">
        Sentence: ${escapeHtml(truncateLabel(sentence))}
      </button>
      <button type="button" class="btn-ghost-dark btn-sm translate-bubble-dismiss">Dismiss</button>
    </div>
  `;
    el.querySelector('.translate-bubble-primary')!.addEventListener('click', () => {
      onPick(primary);
    });
    el.querySelector('.translate-bubble-sentence')!.addEventListener('click', () => {
      onPick(sentence);
    });
    el.querySelector('.translate-bubble-dismiss')!.addEventListener('click', hideTranslateBubble);
  });
}

function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

export function showTranslateBubbleLoading(rect: DOMRect, phrase: string): void {
  showBubble(rect, el => {
    el.innerHTML = `
    <p class="translate-bubble-phrase">${escapeHtml(phrase)}</p>
    <p class="translate-bubble-status"><span class="app-spinner app-spinner--sm" aria-hidden="true"></span> Translating…</p>
  `;
  });
}

export type TranslateBubbleResultOptions = {
  /** Highlighted word when the bubble shows a full-sentence translation. */
  highlightedWord?: string;
};

export function showTranslateBubbleResult(
  rect: DOMRect,
  phrase: string,
  translation: string,
  options?: TranslateBubbleResultOptions
): void {
  const highlightedWord = options?.highlightedWord?.trim() ?? '';

  showBubble(rect, el => {
    const wordLine =
      highlightedWord.length >= 2
        ? `<p class="translate-bubble-word">${escapeHtml(highlightedWord)}</p>`
        : '';
    el.innerHTML = `
    ${wordLine}
    <p class="translate-bubble-phrase">${escapeHtml(phrase)}</p>
    <p class="translate-bubble-result">${escapeHtml(translation)}</p>
    <div class="translate-bubble-actions">
      <button type="button" class="btn-ghost-dark btn-sm translate-bubble-copy">Copy</button>
      <button type="button" class="btn-ghost-dark btn-sm translate-bubble-dismiss">Dismiss</button>
    </div>
  `;
    el.querySelector('.translate-bubble-copy')!.addEventListener('click', () => {
      void copyTextToClipboard(translation);
    });
    el.querySelector('.translate-bubble-dismiss')!.addEventListener('click', hideTranslateBubble);
  });
}

export function showTranslateBubbleError(rect: DOMRect, phrase: string, message: string): void {
  showBubble(rect, el => {
    el.innerHTML = `
    <p class="translate-bubble-phrase">${escapeHtml(phrase)}</p>
    <p class="translate-bubble-error">${escapeHtml(message)}</p>
    <button type="button" class="btn-ghost-dark btn-sm translate-bubble-dismiss">Dismiss</button>
  `;
    el.querySelector('.translate-bubble-dismiss')!.addEventListener('click', hideTranslateBubble);
  });
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
